import Box from '@amzn/awsui-components-core/box';
import { buildEmbeddedAppPath } from 'src/utilities/urlName';
import Icon from '@amzn/awsui-components-core/icon';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { RecentEntityType } from 'src/services/recents/RecentsConstants';
import { useAppHydrator } from 'src/services/recents/hydrators/appHydrator';
import { profileHydrator } from 'src/services/recents/hydrators/profileHydrator';
import { useRecents } from './hooks/useRecents';
import { ENVIRONMENT_VARIABLES, IS_NON_PROD } from 'src/constants/EnvironmentConstants';
import { getNavigationItemsBySection } from 'src/utilities/RouteConfig';
import { QUICK_LINKS_SECTIONS, QuickLinkItem, QuickLinksSection } from './landing-page-constants';

/** Max combined recent items shown in the sidebar. */
const QUICK_LINKS_RECENTS_CAP = 10;

interface QuickLinksPanelProps {
  /** When provided, renders a collapse toggle in the top-right corner. */
  onCollapse?: () => void;
}

/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Sidebar panel with categorized quick links — Recently Visited, Explore, Org Tools, Kingpin, and My Favorites.
 * RECENTLY VISITED merges the user's recent apps and profile links (ranked by score, capped).
 * MY FAVORITES is driven by the user's favoriteApplications resolved against parsedApplications.
 * Internal links use SPA navigation via react-router; external links open in new tab.
 */
const QuickLinksPanel: React.FC<QuickLinksPanelProps> = ({ onCollapse }) => {
  const { favoriteApplications, parsedApplications } = useAppContext();
  const { userLDAPGroups } = useAuth();
  const navigate = useNavigate();

  const appHydrator = useAppHydrator();
  const { scoredRecents: scoredApps } = useRecents(RecentEntityType.APP, appHydrator);
  const { scoredRecents: scoredProfile } = useRecents(RecentEntityType.PROFILE, profileHydrator);

  const sections: QuickLinksSection[] = useMemo(() => {
    // Build "RECENTLY VISITED" — merge apps + profile links, sort by score (latest first), cap.
    type ScoredLink = { link: QuickLinkItem; visitedAt: string };
    const scored: ScoredLink[] = [
      ...scoredApps.map((h) => ({
        link: {
          label: h.data.applicationName,
          href: h.data.embeddingEnabled ? buildEmbeddedAppPath(h.data) : h.data.applicationLink,
          external: !h.data.embeddingEnabled
        },
        visitedAt: h.visitedAt
      })),
      ...scoredProfile.map((h) => ({
        link: { label: h.data.text, href: h.data.href, external: true },
        visitedAt: h.visitedAt
      }))
    ];
    scored.sort((a, b) => b.visitedAt.localeCompare(a.visitedAt));
    const recentLinks = scored.slice(0, QUICK_LINKS_RECENTS_CAP).map((s) => s.link);

    const favoriteLinks: QuickLinkItem[] = favoriteApplications.flatMap((favId) => {
      const app = parsedApplications.find((a) => a.id === favId);
      if (!app || !app.isActive) return [];
      return [
        { label: app.applicationName, href: app.embeddingEnabled ? buildEmbeddedAppPath(app) : app.applicationLink, external: !app.embeddingEnabled }
      ];
    });

    const kingpinNavItems = getNavigationItemsBySection('kingpin', undefined, ENVIRONMENT_VARIABLES.env.Stage);
    const kingpinSection: QuickLinksSection = {
      title: 'KINGPIN',
      links: kingpinNavItems
        .filter((item): item is typeof item & { type: 'link' } => item.type === 'link')
        .map((item) => ({ label: item.text, href: item.href || '', external: item.external || false }))
    };

    const favoritesSection: QuickLinksSection = { title: 'MY FAVORITES', links: favoriteLinks };
    const recentsSection: QuickLinksSection = { title: 'RECENTLY VISITED', links: recentLinks };
    // Gate static quick links by LDAP the same way the side nav / route config does:
    // a link with accessibleTo is shown only if the user is in one of those groups.
    // nonProdOnlyAccessibleTo groups are folded in only in non-prod stages (dev/alpha),
    // while hiddenInProd drops the link on prod alone (alpha/beta keep it).
    // Links without any gate remain visible to everyone. Empty sections are dropped.
    const canSee = (item: QuickLinkItem) => {
      if (item.hiddenInProd && ENVIRONMENT_VARIABLES.env.Stage === 'prod') return false;
      if (!item.accessibleTo && !item.nonProdOnlyAccessibleTo) return true;
      const allowed = [...(item.accessibleTo ?? []), ...(IS_NON_PROD ? item.nonProdOnlyAccessibleTo ?? [] : [])];
      return allowed.some((g) => userLDAPGroups?.includes(g));
    };
    const gatedQuickLinks = QUICK_LINKS_SECTIONS.map((section) => ({ ...section, links: section.links.filter(canSee) })).filter(
      (section) => section.links.length > 0
    );
    return [
      ...gatedQuickLinks,
      ...(kingpinSection.links.length > 0 ? [kingpinSection] : []),
      favoritesSection,
      ...(recentLinks.length > 0 ? [recentsSection] : [])
    ];
  }, [scoredApps, scoredProfile, favoriteApplications, parsedApplications, userLDAPGroups]);

  const handleClick = useCallback(
    (e: React.MouseEvent, link: QuickLinkItem) => {
      if (!link.external) {
        e.preventDefault();
        navigate(link.href);
      }
    },
    [navigate]
  );

  return (
    <div className="quick-links-panel">
      {onCollapse && (
        <span className="quick-links-panel__collapse-btn" onClick={onCollapse} role="button" aria-label="Collapse sidebar">
          <Icon name="angle-left" />
        </span>
      )}
      {sections.map((section: QuickLinksSection, index: number) => (
        <React.Fragment key={section.title}>
          {index > 0 && <hr className="quick-links-panel__divider" />}
          <div className="quick-links-panel__section">
            <Box variant="h4" fontSize="body-s" color="text-body-secondary" fontWeight="bold">
              {section.title}
              {section.external && (
                <span className="quick-links-panel__external-icon">
                  <Icon name="external" size="small" />
                </span>
              )}
            </Box>
            <SpaceBetween size="xxs">
              {section.links.map((link: QuickLinkItem) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="quick-links-panel__link"
                  title={link.tooltip}
                  onClick={(e) => handleClick(e, link)}
                  {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  <Box color="text-body-secondary" fontSize="body-m">
                    {link.label}
                    {link.external && !section.external && (
                      <span className="quick-links-panel__external-icon">
                        <Icon name="external" size="small" />
                      </span>
                    )}
                  </Box>
                </a>
              ))}
            </SpaceBetween>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default QuickLinksPanel;
